/**
 * Smart Card Number Utilities
 * Handles normalization and cleaning of card numbers for matching and search
 */

/**
 * Normalize a card number for storage and comparison
 * Strips leading zeros from numeric portions, removes # prefix, removes /total suffix
 * 
 * Examples:
 * "0715/15" → "715"
 * "#0715" → "715"
 * "TG15" → "TG15"
 * "SV001" → "SV1"
 * "RC15/RC25" → "RC15"
 */
export function normalizeCardNumber(cardNumber: string | null | undefined): string {
  if (!cardNumber) return '';
  
  let cleaned = cardNumber.trim();
  
  // Remove # prefix
  cleaned = cleaned.replace(/^#/, '');
  
  // Remove /total suffix (e.g., /15, /RC25)
  cleaned = cleaned.replace(/\/.*$/, '');
  
  // Handle alphanumeric prefixes (TG, SV, SWSH, RC, etc.) followed by zeros
  // "SV001" → "SV1", "TG015" → "TG15"
  cleaned = cleaned.replace(/^([A-Za-z]+)0+(\d+)$/, '$1$2');
  
  // Handle pure numeric with leading zeros: "0715" → "715"
  cleaned = cleaned.replace(/^0+(\d+)$/, '$1');
  
  return cleaned;
}

/**
 * Clean card number specifically for eBay/marketplace search queries
 * Same as normalize but ensures no leading zeros that could hurt search results
 */
export function cleanCardNumberForSearch(cardNumber: string | null | undefined): string {
  // Use the same normalization - it's already optimized for search
  return normalizeCardNumber(cardNumber);
}

/**
 * Check if two card numbers are equivalent after normalization
 * "715" matches "0715/15" matches "#715"
 */
export function cardNumbersMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  
  return normalizeCardNumber(a) === normalizeCardNumber(b);
}

/**
 * Format card number for display
 * Cleans up but preserves meaningful information
 */
export function formatCardNumberForDisplay(cardNumber: string | null | undefined): string {
  if (!cardNumber) return '';
  
  // Just remove the # prefix and trim, keep the rest for display
  return cardNumber.trim().replace(/^#/, '');
}

/**
 * Parse card number into components for advanced matching
 */
export function parseCardNumber(cardNumber: string | null | undefined): {
  prefix: string;
  number: number | null;
  suffix: string;
  total: string | null;
} {
  if (!cardNumber) {
    return { prefix: '', number: null, suffix: '', total: null };
  }
  
  let cleaned = cardNumber.trim().replace(/^#/, '');
  
  // Extract /total if present
  let total: string | null = null;
  const slashMatch = cleaned.match(/\/(.+)$/);
  if (slashMatch) {
    total = slashMatch[1];
    cleaned = cleaned.replace(/\/.*$/, '');
  }
  
  // Match pattern: optional prefix letters, number, optional suffix letters
  const match = cleaned.match(/^([A-Za-z]*)(\d+)([A-Za-z]*)$/);
  
  if (match) {
    return {
      prefix: match[1],
      number: parseInt(match[2], 10),
      suffix: match[3],
      total
    };
  }
  
  // Couldn't parse - return as-is
  return { prefix: cleaned, number: null, suffix: '', total };
}
